import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  CartState,
  CartItem,
  ShippingAddress,
  ShippingMethod,
  CartSummary,
  CouponCart,
} from '../types/cart';
import { useToastStore } from './useToastStore';
import { useUserStore } from './useUserStore';
import { CreateCartPayload } from '@/types/prestashop';

import { PRESTASHOP_CONFIG } from '@/config/prestashop';
// Variable global para almacenar los carriers
let globalCarriers: any[] = [];

// Función para actualizar los carriers globales
const updateGlobalCarriers = (carriers: any[]) => {
  globalCarriers = carriers;
};

// Función helper que usa los carriers globales
const calculateCartSummaryWithCarriers = (
  items: CartItem[],
  shippingMethod: ShippingMethod | null,
  coupon: CouponCart | null = null
): CartSummary => {
  return calculateCartSummary(items, shippingMethod, coupon, globalCarriers);
};

// Importar el hook de analytics (se usará en las acciones)
let analyticsHook: any = null;

// Función para obtener el hook de analytics de forma lazy
const getAnalytics = () => {
  if (!analyticsHook) {
    // Importación dinámica para evitar problemas de SSR
    import('../hooks/useAnalytics').then(({ useAnalytics }) => {
      analyticsHook = useAnalytics();
    });
  }
  return analyticsHook;
};

interface CartStore extends CartState {
  // PrestaShop Cart
  prestaShopCartId: number | null;
  isPrestaShopCartLoading: boolean;
  prestaShopCartError: string | null;

  // Actions
  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  setShippingAddress: (address: ShippingAddress) => void;
  setBillingAddress: (address: ShippingAddress | null) => void;
  setShippingMethod: (method: ShippingMethod) => void;
  setCoupon: (coupon: CouponCart | null) => void;
  calculateSummary: () => void;
  getItemCount: () => number;
  getTotalItems: () => number;
  beginCheckout: () => void;

  // PrestaShop Cart Actions
  createPrestaShopCart: () => Promise<void>;
  createTemporaryCart: () => Promise<void>;
  syncWithPrestaShopCart: () => Promise<void>;
  loadPrestaShopCart: (cartId: number) => Promise<void>;
  updatePrestaShopCart: () => Promise<void>;
  deletePrestaShopCart: () => Promise<void>;
  getPrestaShopCartId: () => number | null;
  setPrestaShopCartId: (cartId: number) => void;

  // Carrier Actions
  updateCarriers: (carriers: any[]) => void;
}

const calculateCartSummary = (
  items: CartItem[],
  shippingMethod: ShippingMethod | null,
  coupon: CouponCart | null = null,
  carriers: any[] = []
): CartSummary => {
  const totalitems = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const taxesItems = items.reduce((sum, item) => sum + item.tax * item.quantity, 0);
  const subtotalInc = totalitems;
  const subtotalExc = totalitems - taxesItems;
  let shippingExc = 0;
  let shippingInc = 0;
  let handlingPrice = 0;

  let discount = 0;
  if (coupon) {
    if (coupon.freeShipping) {
      shippingInc = 0;
    } else {
      if (coupon.minimumAmount <= subtotalInc) {
        if (coupon.reductionPercent) {
          discount = (subtotalInc * coupon.reductionPercent) / 100;
        } else {
          if (coupon.reductionTax) {
            discount = coupon.reductionAmount;
          } else {
            discount = (subtotalExc * coupon.reductionAmount) / 100;
          }
        }
      }
    }
  }

  const carrierByPrice = carriers?.filter((carrier) => carrier.shipping_method === 2)?.[0];
  const carrierByWeight = carriers?.filter((carrier) => carrier.shipping_method === 1)?.[0];
  const maxDelimiter1 = Math.max(
    ...(carrierByPrice?.delivery_price_ranges?.map((range: any) =>
      parseFloat(range.delimiter1)
    ) || [0])
  );

  const subTotalWithDiscount = subtotalInc - discount;

  if (subTotalWithDiscount >= maxDelimiter1) {
    shippingExc = 0;
    handlingPrice = parseFloat(carrierByPrice?.handling_config?.value || '0');
    const shippingMethod: ShippingMethod = {
      id: carrierByPrice?.id?.toString() || '',
      name: carrierByPrice?.name || '',
      estimatedDays: carrierByPrice?.grade?.toString() || '',
      description: carrierByPrice?.description || '',
      type:
        carrierByPrice?.shipping_method === 1
          ? 'weight'
          : carrierByPrice?.shipping_method === 2
            ? 'price'
            : 'standard',
      isFree: carrierByPrice?.is_free === '1',
      handlingPrice:
        carrierByPrice?.shipping_handling == '1'
          ? parseFloat(carrierByPrice?.handling_config?.value || '0')
          : 0,
      shippingPrice: carrierByPrice?.shipping_price || 0,
    };

    useCartStore.getState().setShippingMethod(shippingMethod);
  } else {
    const sumWeight = items.reduce(
      (sum, item) => sum + parseFloat(item.weight || '0') * item.quantity,
      0
    );
    console.log('sumWeight', sumWeight);
    const idRangeWeight = carrierByWeight?.delivery_weight_ranges?.find(
      (delivery: any) =>
        sumWeight >= parseFloat(delivery.delimiter1) && sumWeight <= parseFloat(delivery.delimiter2)
    )?.id;
    shippingExc = parseFloat(
      carrierByWeight?.deliveries?.find(
        (delivery: any) => delivery.id_range_weight === idRangeWeight
      )?.price || '0'
    );
    handlingPrice = parseFloat(carrierByWeight?.handling_config?.value || '0');
    const shippingMethod: ShippingMethod = {
      id: carrierByWeight.id.toString(),
      name: carrierByWeight.name,
      estimatedDays: carrierByWeight.grade.toString(),
      description: carrierByWeight.description,
      type:
        carrierByWeight.shipping_method === 1
          ? 'weight'
          : carrierByWeight.shipping_method === 2
            ? 'price'
            : 'standard',
      isFree: carrierByWeight.is_free === '1',
      handlingPrice:
        carrierByWeight.shipping_handling == '1'
          ? parseFloat(carrierByWeight.handling_config?.value || '0')
          : 0,
      shippingPrice: carrierByWeight.shipping_price,
    };
    useCartStore.getState().setShippingMethod(shippingMethod);
  }

  shippingExc += handlingPrice;
  const taxesShipping = shippingExc * 0.21;
  shippingInc = shippingExc + taxesShipping;

  const taxes = taxesItems + taxesShipping;

  //const discount = couponCode === '8EXTRA' ? subtotal * 0.08 : 0; // 8% descuento con cupón
  const total = subtotalInc + shippingInc - discount;

  return {
    subtotalProducts: subtotalInc,
    subtotal: subtotalInc - discount,
    shipping: shippingInc,
    taxes,
    discount,
    total,
  };
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial state
      items: [],
      shippingAddress: null,
      billingAddress: null,
      shippingMethod: null,
      couponCode: '',
      coupon: null,
      summary: {
        subtotalProducts: 0,
        subtotal: 0,
        shipping: 0,
        taxes: 0,
        discount: 0,
        total: 0,
      },

      // PrestaShop Cart state
      prestaShopCartId: null,
      isPrestaShopCartLoading: false,
      prestaShopCartError: null,

      // Actions
      addItem: (newItem: CartItem) => {
        set((state) => {
          const existingItemIndex = state.items.findIndex((item) => item.id === newItem.id);
          let newItems;
          let isNewProduct = false;

          if (existingItemIndex >= 0) {
            // Update existing item quantity
            newItems = state.items.map((item, index) =>
              index === existingItemIndex
                ? { ...item, quantity: item.quantity + newItem.quantity }
                : item
            );
          } else {
            // Add new item
            newItems = [...state.items, newItem];
            isNewProduct = true;
          }

          const newSummary = calculateCartSummaryWithCarriers(
            newItems,
            state.shippingMethod,
            state.coupon
          );

          // Show toast notification
          const toastStore = useToastStore.getState();
          const message = isNewProduct
            ? `${newItem.name} agregado al carrito`
            : `Se actualizó la cantidad de ${newItem.name} en el carrito`;
          toastStore.showToast(message, 'success');

          return {
            items: newItems,
            summary: newSummary,
          };
        });

        // Sincronizar con PrestaShop si existe un carrito
        const state = get();
        if (state.prestaShopCartId) {
          get().updatePrestaShopCart();
        }
      },

      removeItem: (itemId: string) => {
        let itemToRemove: CartItem | undefined;

        set((state) => {
          itemToRemove = state.items.find((item) => item.id === itemId);
          const newItems = state.items.filter((item) => item.id !== itemId);
          const newSummary = calculateCartSummaryWithCarriers(
            newItems,
            state.shippingMethod,
            state.coupon
          );

          // Trackear evento de remove_from_cart si existe el item
          if (itemToRemove) {
            const analytics = getAnalytics();
            if (analytics) {
              analytics.trackRemoveFromCart({
                currency: 'EUR',
                value: itemToRemove.price * itemToRemove.quantity,
                items: [
                  {
                    item_id: itemToRemove.id,
                    item_name: itemToRemove.name,
                    item_category: itemToRemove.category,
                    price: itemToRemove.price,
                    quantity: itemToRemove.quantity,
                  },
                ],
              });
            }
          }

          return {
            items: newItems,
            summary: newSummary,
          };
        });

        // Sincronizar con PrestaShop si existe un carrito
        const state = get();
        if (state.prestaShopCartId && itemToRemove) {
          // Remover producto de PrestaShop
          fetch(`/api/es/carts/${state.prestaShopCartId}/products`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              productId: parseInt(itemId),
              productAttributeId: 0,
            }),
          }).catch((error) => {
            console.error('Error removiendo producto de PrestaShop:', error);
          });
        }
      },

      updateQuantity: (itemId: string, quantity: number) => {
        set((state) => {
          const newItems = state.items.map((item) =>
            item.id === itemId ? { ...item, quantity } : item
          );
          const newSummary = calculateCartSummaryWithCarriers(
            newItems,
            state.shippingMethod,
            state.coupon
          );

          return {
            items: newItems,
            summary: newSummary,
          };
        });

        // Sincronizar con PrestaShop si existe un carrito
        const state = get();
        if (state.prestaShopCartId) {
          fetch(`/api/es/carts/${state.prestaShopCartId}/products`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              productId: parseInt(itemId),
              quantity: quantity,
              productAttributeId: 0,
            }),
          }).catch((error) => {
            console.error('Error actualizando cantidad en PrestaShop:', error);
          });
        }
      },

      clearCart: () => {
        set({
          items: [],
          summary: {
            subtotalProducts: 0,
            subtotal: 0,
            shipping: 0,
            taxes: 0,
            discount: 0,
            total: 0,
          },
          shippingAddress: null,
          billingAddress: null,
          shippingMethod: null,
          coupon: null,
        });

        // Limpiar carrito de PrestaShop si existe
        const state = get();
        state.prestaShopCartId = null;
        state.isPrestaShopCartLoading = false;
        state.prestaShopCartError = null;
        set(state);
      },

      setShippingAddress: (address: ShippingAddress) => {
        set({ shippingAddress: address });
      },

      setBillingAddress: (address: ShippingAddress | null) => {
        set({ billingAddress: address });
      },

      setShippingMethod: (method: ShippingMethod) => {
        set((state) => {
          const prestaShopCartId = state.prestaShopCartId;

          if (prestaShopCartId && method) {
            // Llamar al endpoint directamente sin usar hooks
            fetch(`/api/es/carts/${prestaShopCartId}`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id_carrier: parseInt(method.id),
              }),
            }).catch((error) => {
              console.error('Error actualizando carrier en PrestaShop:', error);
            });
          }

          //const newSummary = calculateCartSummaryWithCarriers(state.items, method, state.coupon);
          return {
            shippingMethod: method,
            //summary: newSummary,
          };
        });
      },
      setCoupon: (coupon: CouponCart | null) => {
        set((state) => {
          const newSummary = calculateCartSummaryWithCarriers(
            state.items,
            state.shippingMethod,
            coupon
          );
          return {
            coupon: coupon,
            summary: newSummary,
          };
        });
      },
      calculateSummary: () => {
        const state = get();
        const newSummary = calculateCartSummaryWithCarriers(
          state.items,
          state.shippingMethod,
          state.coupon
        );
        set({ summary: newSummary });
      },

      getItemCount: () => {
        return get().items.length;
      },

      getTotalItems: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      beginCheckout: () => {
        const state = get();
        const analytics = getAnalytics();
        if (analytics && state.items.length > 0) {
          analytics.trackBeginCheckout({
            currency: 'EUR',
            value: state.summary.total,
            items: state.items.map((item) => ({
              item_id: item.id,
              item_name: item.name,
              item_category: item.category,
              price: item.price,
              quantity: item.quantity,
            })),
          });
        }
      },

      // PrestaShop Cart Actions
      createPrestaShopCart: async () => {
        const state = get();
        const { profile } = useUserStore.getState();
        const carriierId = state.shippingMethod?.id;

        if (!profile?.id) {
          set({ prestaShopCartError: 'Usuario no autenticado' });
          return;
        }

        set({ isPrestaShopCartLoading: true, prestaShopCartError: null });

        try {
          // Convertir items locales a cart_rows para PrestaShop
          const cartRows = state.items.map((item) => ({
            id_product: item.id,
            id_product_attribute: 0,
            id_address_delivery: 0,
            id_customization: 0,
            quantity: item.quantity,
          }));

          const cartData: CreateCartPayload = {
            id_customer: profile.id,
            id_address_delivery: 0, // En blanco - no es obligatorio
            id_address_invoice: 0, // En blanco - no es obligatorio
            id_currency: PRESTASHOP_CONFIG.CURRENCY_ID,
            id_lang: PRESTASHOP_CONFIG.LANGUAGE_ID,
            id_carrier: parseInt(carriierId || '0'),
            associations: {
              cart_rows: cartRows,
            },
          };

          console.log('🛒 Creando carrito con productos:', cartRows);

          // Llamar a la API para crear el carrito
          const response = await fetch('/api/es/carts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(cartData),
          });

          if (response.ok) {
            const data = await response.json();
            const cart = data.data;

            if (cart) {
              console.log('✅ Carrito creado en PrestaShop con ID:', cart.id);
              set({
                prestaShopCartId: cart.id,
                isPrestaShopCartLoading: false,
              });

              // No necesitamos sincronizar porque ya incluimos los productos
              console.log('✅ Carrito creado exitosamente con productos incluidos');
            } else {
              set({
                prestaShopCartError: 'No se pudo crear el carrito en PrestaShop',
                isPrestaShopCartLoading: false,
              });
            }
          } else {
            const errorData = await response.json();
            set({
              prestaShopCartError: errorData.error || 'Error creando carrito',
              isPrestaShopCartLoading: false,
            });
          }
        } catch (error) {
          console.error('❌ Error creando carrito en PrestaShop:', error);
          set({
            prestaShopCartError: error instanceof Error ? error.message : 'Error creando carrito',
            isPrestaShopCartLoading: false,
          });
        }
      },

      // Función para crear carrito temporal (usuarios no autenticados)
      createTemporaryCart: async () => {
        set({ isPrestaShopCartLoading: true, prestaShopCartError: null });

        try {
          // Convertir items locales a cart_rows para PrestaShop
          const state = get();
          const carriierId = state.shippingMethod?.id;
          const cartRows = state.items.map((item) => ({
            id_product: item.id,
            id_product_attribute: 0,
            id_address_delivery: 0,
            id_customization: 0,
            quantity: item.quantity,
          }));

          const cartData: CreateCartPayload = {
            id_customer: 0, // Usuario invitado
            id_address_delivery: 0,
            id_address_invoice: 0,
            id_currency: PRESTASHOP_CONFIG.CURRENCY_ID,
            id_lang: PRESTASHOP_CONFIG.LANGUAGE_ID,
            id_carrier: parseInt(carriierId || '0'),
            associations: {
              cart_rows: cartRows,
            },
          };

          console.log('🛒 Creando carrito temporal con productos:', cartRows);

          // Llamar a la API para crear el carrito temporal
          const response = await fetch('/api/es/carts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(cartData),
          });

          if (response.ok) {
            const data = await response.json();
            const cart = data.data;

            if (cart) {
              console.log('✅ Carrito temporal creado en PrestaShop con ID:', cart.id);
              set({
                prestaShopCartId: cart.id,
                isPrestaShopCartLoading: false,
              });

              // No necesitamos sincronizar porque ya incluimos los productos
              console.log('✅ Carrito temporal creado exitosamente con productos incluidos');
            } else {
              set({
                prestaShopCartError: 'No se pudo crear el carrito temporal en PrestaShop',
                isPrestaShopCartLoading: false,
              });
            }
          } else {
            const errorData = await response.json();
            set({
              prestaShopCartError: errorData.error || 'Error creando carrito temporal',
              isPrestaShopCartLoading: false,
            });
          }
        } catch (error) {
          console.error('❌ Error creando carrito temporal en PrestaShop:', error);
          set({
            prestaShopCartError:
              error instanceof Error ? error.message : 'Error creando carrito temporal',
            isPrestaShopCartLoading: false,
          });
        }
      },

      syncWithPrestaShopCart: async () => {
        const state = get();

        if (!state.prestaShopCartId) {
          return;
        }

        set({ isPrestaShopCartLoading: true });

        try {
          // Agregar todos los items locales al carrito de PrestaShop
          for (const item of state.items) {
            const response = await fetch(`/api/es/carts/${state.prestaShopCartId}/products`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                productId: parseInt(item.id),
                quantity: item.quantity,
                productAttributeId: 0,
                addressDeliveryId: 0,
              }),
            });

            if (!response.ok) {
              throw new Error(`Error agregando producto ${item.id} al carrito`);
            }
          }

          set({ isPrestaShopCartLoading: false });
        } catch (error) {
          set({
            prestaShopCartError:
              error instanceof Error ? error.message : 'Error sincronizando carrito',
            isPrestaShopCartLoading: false,
          });
        }
      },

      loadPrestaShopCart: async (cartId: number) => {
        set({ isPrestaShopCartLoading: true, prestaShopCartError: null });

        try {
          // Llamar a la API para obtener el carrito
          const cartResponse = await fetch(`/api/es/carts/${cartId}`);
          if (!cartResponse.ok) {
            set({
              prestaShopCartError: 'Carrito no encontrado',
              isPrestaShopCartLoading: false,
            });
            return;
          }

          const cartData = await cartResponse.json();
          const cart = cartData.data;

          if (cart) {
            set({
              prestaShopCartId: cart.id,
              isPrestaShopCartLoading: false,
            });

            // Cargar productos del carrito de PrestaShop
            const productsResponse = await fetch(`/api/es/carts/${cartId}/products`);
            if (productsResponse.ok) {
              const productsData = await productsResponse.json();
              const products = productsData.data;

              // Convertir productos de PrestaShop a CartItems locales
              const localItems: CartItem[] = products.map((product: any) => ({
                id: product.id.toString(),
                name: product.name || 'Producto sin nombre',
                image: product.image || '',
                price: product.price_with_tax || 0,
                quantity: product.quantity,
                weight: product.weight,
                sku: product.reference,
              }));

              set({
                items: localItems,
                isPrestaShopCartLoading: false,
              });

              // Recalcular resumen
              get().calculateSummary();
            } else {
              set({
                prestaShopCartError: 'Error cargando productos del carrito',
                isPrestaShopCartLoading: false,
              });
            }
          } else {
            set({
              prestaShopCartError: 'Carrito no encontrado',
              isPrestaShopCartLoading: false,
            });
          }
        } catch (error) {
          set({
            prestaShopCartError: error instanceof Error ? error.message : 'Error cargando carrito',
            isPrestaShopCartLoading: false,
          });
        }
      },

      updatePrestaShopCart: async () => {
        const state = get();

        if (!state.prestaShopCartId) {
          return;
        }

        set({ isPrestaShopCartLoading: true });

        try {
          // Actualizar cada item en el carrito de PrestaShop
          for (const item of state.items) {
            const response = await fetch(`/api/es/carts/${state.prestaShopCartId}/products`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                productId: parseInt(item.id),
                quantity: item.quantity,
                productAttributeId: 0,
              }),
            });

            if (!response.ok) {
              throw new Error(`Error actualizando producto ${item.id} en el carrito`);
            }
          }

          set({ isPrestaShopCartLoading: false });
        } catch (error) {
          set({
            prestaShopCartError:
              error instanceof Error ? error.message : 'Error actualizando carrito',
            isPrestaShopCartLoading: false,
          });
        }
      },

      deletePrestaShopCart: async () => {
        const state = get();

        if (!state.prestaShopCartId) {
          return;
        }

        set({ isPrestaShopCartLoading: true });

        try {
          const response = await fetch(`/api/es/carts/${state.prestaShopCartId}`, {
            method: 'DELETE',
          });

          if (response.ok) {
            set({
              prestaShopCartId: null,
              isPrestaShopCartLoading: false,
            });
          } else {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error eliminando carrito');
          }
        } catch (error) {
          set({
            prestaShopCartError:
              error instanceof Error ? error.message : 'Error eliminando carrito',
            isPrestaShopCartLoading: false,
          });
        }
      },

      getPrestaShopCartId: () => {
        return get().prestaShopCartId;
      },

      setPrestaShopCartId: (cartId: number) => {
        set({ prestaShopCartId: cartId });
      },

      updateCarriers: (carriers: any[]) => {
        updateGlobalCarriers(carriers);
        // Recalcular el summary con los nuevos carriers
        const state = get();
        const newSummary = calculateCartSummaryWithCarriers(
          state.items,
          state.shippingMethod,
          state.coupon
        );
        set({ summary: newSummary });
      },
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({
        items: state.items,
        shippingAddress: state.shippingAddress,
        billingAddress: state.billingAddress,
        shippingMethod: state.shippingMethod,
        coupon: state.coupon,
        prestaShopCartId: state.prestaShopCartId,
        summary: state.summary,
      }),
      onRehydrateStorage: () => (state) => {
        // Recalcular el summary cuando se restaura el estado desde localStorage
        if (state) {
          const newSummary = calculateCartSummaryWithCarriers(
            state.items,
            state.shippingMethod,
            state.coupon
          );
          state.summary = newSummary;
          state.coupon = state.coupon;
        }
      },
    }
  )
);

// Exportar la función para actualizar carriers
export { updateGlobalCarriers };
